/**
 * Review Utils
 *
 * Helper functions for flight review operations including:
 * - Fare selection utilities
 * - Stepper state management
 * - Data transformation utilities
 */
import type {
  FareOption,
  FlightsReviewInterstitialTemplateData,
  StepperData,
  ReviewTravellerTabHeader,
} from '@contracts';
import type { SelectedFareState } from '@/features/flights/redux/types/flightsTypes';
import { logger } from '@/shared/utils/logger';

/**
 * Selected fare info for interstitial template
 * Key format: "{journeyIndex}-{legIndex}"
 * Value: contextHashKey from the selected fare
 */
export type InterstitialSelectedFares = Record<string, string>;

/**
 * Extract contextHashKey from a fare option's select_fare action
 */
export function getContextHashKeyFromFareOption(fareOption: FareOption): string | undefined {
  const selectFareAction = fareOption.actions?.find(
    (action) => action.actionType === 'select_fare',
  );
  return selectFareAction?.actionPayload?.['contextHashKey'] as string | undefined;
}

/**
 * Get selected fare's contextHashKey from fare options array
 */
export function getSelectedFareContextHashKey(fareOptions: FareOption[]): string | undefined {
  const selectedFare = fareOptions.find((fare) => fare.selected);
  if (selectedFare) {
    return getContextHashKeyFromFareOption(selectedFare);
  }
  return undefined;
}

/**
 * Extract selected fares from API response
 * Matches previous Zustand implementation logic
 */
export function extractSelectedFaresFromResponse(
  journeyFareOptions: unknown,
  fareOptions: unknown,
): SelectedFareState {
  const selectedFares: SelectedFareState = {};

  // Extract from journeyFareOptions if available (preferred for roundtrip)
  if (Array.isArray(journeyFareOptions) && journeyFareOptions.length > 0) {
    journeyFareOptions.forEach((journey, idx) => {
      if (journey && typeof journey === 'object' && 'fareOptions' in journey) {
        const journeyFareOptionsArray = journey.fareOptions;
        if (Array.isArray(journeyFareOptionsArray) && journeyFareOptionsArray.length > 0) {
          // Type assertion: journey.fareOptions should be FareOption[] based on schema
          const contextHashKey = getSelectedFareContextHashKey(
            journeyFareOptionsArray as FareOption[],
          );
          if (contextHashKey) {
            selectedFares[idx] = contextHashKey;
          }
        }
      }
    });
  } else if (Array.isArray(fareOptions) && fareOptions.length > 0) {
    // Fallback to fareOptions for one-way flights
    // Type assertion: fareOptions should be FareOption[] based on schema
    const contextHashKey = getSelectedFareContextHashKey(fareOptions as FareOption[]);
    if (contextHashKey) {
      selectedFares[0] = contextHashKey;
    }
  }

  return selectedFares;
}

/**
 * Build combined context hash key for roundtrip fare selection
 *
 * For roundtrip flights:
 * - If selecting first fare (journey 0): use just that fare's contextHashKey
 * - If selecting second fare (journey 1) and journey 0 is already selected: combine both
 * - Always combine in order: journey0~~~journey1
 */
export function buildCombinedContextHashKey(
  clickedContextHashKey: string,
  journeyIndex: number,
  selectedFares: SelectedFareState,
  isRoundTrip: boolean,
): string {
  if (!isRoundTrip) {
    return clickedContextHashKey;
  }

  const otherJourneyIndex = journeyIndex === 0 ? 1 : 0;
  const otherFareKey = selectedFares[otherJourneyIndex];

  if (!otherFareKey) {
    return clickedContextHashKey;
  }

  // Combine with ~~~ separator as expected by backend
  return journeyIndex === 0
    ? `${clickedContextHashKey}~~~${otherFareKey}`
    : `${otherFareKey}~~~${clickedContextHashKey}`;
}

// ============================================================================
// Interstitial Template Utilities
// ============================================================================

/**
 * Extract selected fares from FlightsReviewInterstitialTemplateData
 *
 * Loops through:
 * - journeyList (each journey has journeyIndex)
 * - legListCategories (each category - Economy, Premium Economy, Business, First)
 * - legList (each leg has legIndex)
 * - fareList (each fare option)
 *
 * For each leg, finds the selected fare (or first fare if none selected)
 * and extracts the contextHashKey from selectFareCTA.actionPayload.contextHashKey
 *
 * @param data - FlightsReviewInterstitialTemplateData from the API response
 * @returns Record<string, string> where key is "{journeyIndex}-{legIndex}" and value is contextHashKey
 */
export function extractSelectedFaresFromInterstitial(
  data: FlightsReviewInterstitialTemplateData,
): InterstitialSelectedFares {
  const selectedFares: InterstitialSelectedFares = {};
  const journeyList = data.journeyList ?? [];

  journeyList.forEach((journey, journeyIndex) => {
    const legListCategories = journey.legListCategories ?? [];

    legListCategories.forEach((category) => {
      // Only process selected category (or first category if none selected)
      if (!category.selected) return;

      const legList = category.legList ?? [];

      legList.forEach((leg, legIndex) => {
        const fareList = leg.fareList ?? [];

        // Find selected fare or use first fare
        const selectedFare = fareList.find((fare) => fare.selected) ?? fareList[0];

        if (selectedFare) {
          // Extract contextHashKey from selectFareCTA.actionPayload
          const contextHashKey = selectedFare.selectFareCTA?.actionPayload?.contextHashKey;

          if (contextHashKey) {
            const key = `${journeyIndex}-${legIndex}`;
            selectedFares[key] = contextHashKey;
          }
        }
      });
    });
  });

  return selectedFares;
}

/**
 * Result of extracting selected fares with combo string
 */
export interface ExtractedFareSelection {
  selectedFares: InterstitialSelectedFares;
  selectedFareCombo: string;
}

/**
 * Extract selected fares from all categories in FlightsReviewInterstitialTemplateData
 *
 * Creates a single combined leg list by merging fare lists from legs at the same index
 * across all categories (Economy, Premium Economy, Business, First).
 * Legs at the same index are combined into one leg with all fares merged.
 *
 * @param data - FlightsReviewInterstitialTemplateData from the API response
 * @returns Record<string, string> where key is "{journeyIndex}-{legIndex}" and value is contextHashKey
 */
export function extractSelectedFares(
  data: FlightsReviewInterstitialTemplateData,
): InterstitialSelectedFares {
  const { selectedFares } = extractSelectedFaresWithCombo(data);
  return selectedFares;
}

/**
 * Extract selected fares AND selected fare combo from FlightsReviewInterstitialTemplateData
 *
 * Creates a single combined leg list by merging fare lists from legs at the same index
 * across all categories (Economy, Premium Economy, Business, First).
 * Legs at the same index are combined into one leg with all fares merged.
 *
 * Returns both:
 * - selectedFares: Record<string, string> where key is "{journeyIndex}-{legIndex}" and value is contextHashKey
 * - selectedFareCombo: string in format "journeyIndex-legIndex-fareId|..." (e.g., "0-0-0|1-0-1|2-0-2")
 *   Note: Uses fare.fareID instead of array index for consistency
 *
 * @param data - FlightsReviewInterstitialTemplateData from the API response
 * @returns ExtractedFareSelection containing both selectedFares and selectedFareCombo
 */
export function extractSelectedFaresWithCombo(
  data: FlightsReviewInterstitialTemplateData,
): ExtractedFareSelection {
  const selectedFares: InterstitialSelectedFares = {};
  const fareIds: Record<string, string> = {};
  const journeyList = data.journeyList ?? [];

  journeyList.forEach((journey, journeyIndex) => {
    const legListCategories = journey.legListCategories ?? [];

    // Create combined leg list: merge fare lists from legs at the same index across all categories
    // Key: legIndex (as string), Value: combined fare list from all categories for that leg index
    type FareList = NonNullable<
      NonNullable<(typeof legListCategories)[0]['legList']>[0]['fareList']
    >;
    const combinedFaresByLegIndex: Record<string, FareList> = {};

    legListCategories.forEach((category) => {
      const legList = category.legList ?? [];

      legList.forEach((leg, legIndex) => {
        const currentFareList = leg.fareList ?? [];
        const legKey = String(legIndex);
        const existingFares = combinedFaresByLegIndex[legKey];

        if (existingFares) {
          // Merge fare lists - combine fares from all categories for the same leg index
          combinedFaresByLegIndex[legKey] = [...existingFares, ...currentFareList];
        } else {
          // First category for this leg index - initialize with current leg's fares
          combinedFaresByLegIndex[legKey] = [...currentFareList];
        }
      });
    });

    logger.warn('[extractSelectedFares] combinedFaresByLegIndex', combinedFaresByLegIndex);

    // Process combined fare lists to extract selected fares and their fareIDs
    Object.entries(combinedFaresByLegIndex).forEach(([legKey, combinedFareList]) => {
      // Find selected fare or use first fare
      const selectedFare = combinedFareList.find((fare) => fare.selected) ?? combinedFareList[0];

      if (selectedFare) {
        const key = `${journeyIndex}-${legKey}`;

        // Use contextHashKey when available; fall back to fareID-based synthetic key
        // for round-trip/multi-city where individual fares lack contextHashKey
        // (the combined rKey lives in fareMap instead)
        // Note: Use || not ?? — API may return fareID as number 0 or empty string '',
        // which ?? preserves; we need string '0' for fareIds and consistent comparisons.
        // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
        selectedFares[key] = selectedFare.contextHashKey || `fareId:${selectedFare.fareID ?? '0'}`;
        fareIds[key] = selectedFare.fareID ?? '0';
      }
    });
  });

  // Build the fare combo string from fare IDs
  const selectedFareCombo = buildFareComboFromIndices(fareIds);

  return { selectedFares, selectedFareCombo };
}

/**
 * Build fare combo string from fare IDs
 * Format: "journeyIndex-legIndex-fareId|journeyIndex-legIndex-fareId|..."
 * Example: "0-0-0|1-0-1|2-0-2"
 *
 * @param fareIds - Record of fareKey (journeyIndex-legIndex) to fareId (from fare.fareID)
 * @returns Sorted combo string
 */
export function buildFareComboFromIndices(fareIds: Record<string, string>): string {
  const entries = Object.entries(fareIds);
  if (entries.length === 0) return '';

  // Sort by journeyIndex, then legIndex
  entries.sort(([a], [b]) => {
    const [aJourney, aLeg] = a.split('-').map(Number);
    const [bJourney, bLeg] = b.split('-').map(Number);
    if (aJourney !== bJourney) return (aJourney ?? 0) - (bJourney ?? 0);
    return (aLeg ?? 0) - (bLeg ?? 0);
  });

  return entries.map(([key, fareId]) => `${key}-${fareId}`).join('|');
}

/**
 * Get contextHashKey for a specific fare in the interstitial
 *
 * @param fare - The fare option
 * @returns contextHashKey or undefined
 */
export function getInterstitialFareContextHashKey(
  fare: FlightsReviewInterstitialTemplateData['journeyList'] extends (infer J)[]
    ? J extends { legListCategories?: (infer C)[] }
      ? C extends { legList?: (infer L)[] }
        ? L extends { fareList?: (infer F)[] }
          ? F
          : never
        : never
      : never
    : never,
): string | undefined {
  if (fare && typeof fare === 'object' && 'selectFareCTA' in fare) {
    const fareOption = fare as { selectFareCTA?: { actionPayload?: { contextHashKey?: string } } };
    return fareOption.selectFareCTA?.actionPayload?.contextHashKey;
  }
  return undefined;
}

/**
 * Build combined context hash key for interstitial fare selection
 *
 * For multi-city/roundtrip flights, combines all selected fares' contextHashKeys
 * with ~~~ separator
 *
 * @param selectedFares - Record of selected fares (key: "{journeyIndex}-{legIndex}", value: contextHashKey)
 * @returns Combined contextHashKey string
 */
export function buildInterstitialCombinedContextHashKey(
  selectedFares: InterstitialSelectedFares,
): string {
  // Sort keys to ensure consistent ordering (by journey index, then leg index)
  const sortedKeys = Object.keys(selectedFares).sort((a, b) => {
    const [aJourney, aLeg] = a.split('-').map(Number);
    const [bJourney, bLeg] = b.split('-').map(Number);

    if (aJourney !== bJourney) {
      return (aJourney ?? 0) - (bJourney ?? 0);
    }
    return (aLeg ?? 0) - (bLeg ?? 0);
  });

  // Combine real contextHashKeys with ~~~ separator (exclude synthetic fareId: keys)
  const contextHashKeys = sortedKeys
    .map((key) => selectedFares[key])
    .filter((value): value is string => typeof value === 'string' && value.length > 0)
    .filter((value) => !value.startsWith('fareId:'));

  return contextHashKeys.join('~~~');
}

// ============================================================================
// Stepper Types
// ============================================================================

/** Main stepper step types */
export type MainStepType = 'traveller' | 'ancillary' | 'coupons' | 'payment';

/** Step status types */
export type StepStatus = 'completed' | 'active' | 'pending';

/** Journey information for stepper display */
export interface JourneyInfo {
  readonly origin: string;
  readonly destination: string;
  readonly route: {
    from: { code: string; city: string; country: string; displayName: string };
    to: { code: string; city: string; country: string; displayName: string };
  };
}

/** Parameters for calculating step status */
export interface StepStatusParams {
  mainStep: MainStepType;
  activeAncillaryJourneyIndex: number;
  totalSteps: number;
}

// ============================================================================
// Stepper Constants
// ============================================================================

/** Default stepper labels */
export const STEPPER_LABELS = {
  TRAVELLERS: 'SELECT TRAVELLERS',
  TRAVELLERS_SUBTITLE: 'Select travellers',
  ANCILLARY: 'SELECT SEAT, MEAL BAGGAGE & ADD-ONS',
  ANCILLARY_SUBTITLE: 'Select seat',
  COUPONS: 'COUPONS AND OFFERS',
  PAYMENT: 'PAYMENT',
} as const;

// ============================================================================
// Stepper Helper Functions
// ============================================================================

/**
 * Create a default route structure for a journey
 */
export function createDefaultRoute(origin: string, destination: string): JourneyInfo['route'] {
  return {
    from: { code: origin, city: origin, country: '', displayName: origin },
    to: { code: destination, city: destination, country: '', displayName: destination },
  };
}

/**
 * Calculate the status of a step based on current navigation state.
 * Uses stepId when available (backend-driven), falls back to positional heuristics.
 */
export function getStepStatus(
  stepIndex: number,
  isJourneyStep: boolean,
  params: StepStatusParams,
  journeyIndex?: number,
  stepId?: string,
): StepStatus {
  const { mainStep, activeAncillaryJourneyIndex, totalSteps } = params;

  // Step ordering for comparison: traveller < ancillary < coupons < payment
  const STEP_ORDER: Record<string, number> = {
    traveller: 0,
    ancillary: 1,
    coupons: 2,
    payment: 3,
  };

  const currentOrder = STEP_ORDER[mainStep] ?? 0;

  // When stepId is available, use semantic identification
  if (stepId) {
    const stepOrder = STEP_ORDER[stepId] ?? 0;

    if (stepId === 'ancillary' && journeyIndex !== undefined) {
      if (currentOrder < stepOrder) return 'pending';
      if (mainStep === 'ancillary') {
        if (journeyIndex < activeAncillaryJourneyIndex) return 'completed';
        if (journeyIndex === activeAncillaryJourneyIndex) return 'active';
        return 'pending';
      }
      return 'completed';
    }

    if (stepId === mainStep) return 'active';
    if (stepOrder < currentOrder) return 'completed';
    return 'pending';
  }

  // Fallback: positional heuristics (backward compatibility)
  if (stepIndex === 0) {
    return mainStep === 'traveller' ? 'active' : 'completed';
  }

  if (isJourneyStep && journeyIndex !== undefined) {
    if (mainStep === 'traveller') return 'pending';
    if (mainStep === 'ancillary') {
      if (journeyIndex < activeAncillaryJourneyIndex) return 'completed';
      if (journeyIndex === activeAncillaryJourneyIndex) return 'active';
      return 'pending';
    }
    return 'completed';
  }

  if (stepIndex === totalSteps - 2) {
    if (mainStep === 'coupons') return 'active';
    if (mainStep === 'payment') return 'completed';
    return 'pending';
  }

  if (stepIndex === totalSteps - 1) {
    return mainStep === 'payment' ? 'completed' : 'pending';
  }

  return 'pending';
}

/**
 * Check if a step is a journey/ancillary step.
 * Uses stepId when available, falls back to subSteps presence.
 */
export function isStepperJourneyStep(step: StepperData): boolean {
  return step.stepId === 'ancillary' || Boolean(step.subSteps && step.subSteps.length > 0);
}

/**
 * Count the number of journey steps in stepper data
 */
export function countJourneySteps(stepperData: StepperData[]): number {
  return stepperData.filter(isStepperJourneyStep).length;
}

// ============================================================================
// Stepper Data Transformation
// ============================================================================

/**
 * Update stepper data with current step statuses
 *
 * Takes the base stepper data from API and updates the status of each step
 * based on the current navigation state.
 */
export function updateStepperStatuses(
  stepperData: StepperData[],
  params: StepStatusParams,
): StepperData[] {
  let journeyIndex = 0;
  const totalSteps = stepperData.length;

  return stepperData.map((step, stepIndex) => {
    const isJourney = isStepperJourneyStep(step) || step.stepId === 'ancillary';
    const currentJourneyIndex = isJourney ? (step.journeyIndex ?? journeyIndex) : undefined;

    if (isJourney) {
      journeyIndex++;
    }

    const statusParams = { ...params, totalSteps };

    return {
      ...step,
      status: getStepStatus(stepIndex, isJourney, statusParams, currentJourneyIndex, step.stepId),
      subSteps: step.subSteps?.map((subStep) => ({
        ...subStep,
        status: getStepStatus(stepIndex, true, statusParams, currentJourneyIndex, step.stepId),
      })),
    };
  });
}

/**
 * Parse tab headers into journey information
 *
 * Converts legacy tabHeaders format (e.g., "DEL - BOM") into structured journey info.
 */
export function parseTabHeadersToJourneys(tabHeaders: ReviewTravellerTabHeader[]): JourneyInfo[] {
  if (tabHeaders.length === 0) {
    return [
      {
        origin: 'Origin',
        destination: 'Destination',
        route: createDefaultRoute('Origin', 'Destination'),
      },
    ];
  }

  return tabHeaders.map((tab) => {
    const parts = (tab.title ?? '').split('-').map((s) => s.trim());
    const origin = parts[0] ?? 'Origin';
    const destination = parts[1] ?? 'Destination';
    return {
      origin,
      destination,
      route: createDefaultRoute(origin, destination),
    };
  });
}

/**
 * Build stepper data from legacy tabHeaders
 *
 * Creates stepper data structure when API doesn't provide stepperData.
 * Used for backward compatibility with older API responses.
 */
export function buildStepperFromTabHeaders(
  tabHeaders: ReviewTravellerTabHeader[],
  params: StepStatusParams,
): StepperData[] {
  const journeys = parseTabHeadersToJourneys(tabHeaders);
  const totalSteps = journeys.length + 3; // traveller + journeys + coupons + payment
  const statusParams = { ...params, totalSteps };

  const steps: StepperData[] = [
    // Traveller step
    {
      label: STEPPER_LABELS.TRAVELLERS,
      subtitle: STEPPER_LABELS.TRAVELLERS_SUBTITLE,
      status: getStepStatus(0, false, statusParams),
    },
    // Journey steps
    ...journeys.map((journey, idx) => ({
      label: `${journey.origin} → ${journey.destination}`,
      status: getStepStatus(idx + 1, true, statusParams, idx),
      subSteps: [
        {
          label: STEPPER_LABELS.ANCILLARY,
          subtitle: STEPPER_LABELS.ANCILLARY_SUBTITLE,
          status: getStepStatus(idx + 1, true, statusParams, idx),
          route: journey.route,
        },
      ],
    })),
    // Coupons step
    {
      label: STEPPER_LABELS.COUPONS,
      status: getStepStatus(journeys.length + 1, false, statusParams),
    },
    // Payment step
    {
      label: STEPPER_LABELS.PAYMENT,
      status: getStepStatus(journeys.length + 2, false, statusParams),
    },
  ];

  return steps;
}

/**
 * Compute stepper data with updated statuses
 *
 * Main entry point for getting stepper data. Uses Redux data when available,
 * falls back to building from tabHeaders for backward compatibility.
 */
export function computeStepperData(
  reduxStepperData: StepperData[] | null,
  tabHeaders: ReviewTravellerTabHeader[],
  params: StepStatusParams,
): StepperData[] {
  // Use Redux stepper data when available
  if (reduxStepperData && reduxStepperData.length > 0) {
    return updateStepperStatuses(reduxStepperData, params);
  }

  // Fallback: Build from legacy tabHeaders
  return buildStepperFromTabHeaders(tabHeaders, params);
}

/**
 * Calculate total journeys from stepper data or tabHeaders
 */
export function calculateTotalJourneys(
  reduxStepperData: StepperData[] | null,
  tabHeaders: ReviewTravellerTabHeader[],
): number {
  if (reduxStepperData && reduxStepperData.length > 0) {
    return countJourneySteps(reduxStepperData);
  }
  return tabHeaders.length > 0 ? tabHeaders.length : 1;
}
